import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Activity, Banknote, Recycle, TrendingDown, Award, AlertTriangle, ShieldCheck, Layers
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell
} from "recharts";

const Stat = ({ icon: Icon, label, value, sub, accent = "emerald", testid }) => (
  <div data-testid={testid} className="border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-transform bg-white">
    <div className="flex items-start justify-between">
      <div className="overline text-slate-500">{label}</div>
      <Icon className={`w-4 h-4 text-${accent}-700`} />
    </div>
    <div className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/executive").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div data-testid="dashboard-loading" className="text-slate-500">Loading executive dashboard…</div>;
  if (!data) return <div className="text-slate-500">No data.</div>;

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-emerald-800">Executive Dashboard</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-1">
            Currency Carbon Intelligence
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Verified, batch-level evidence of the carbon footprint of every Philippine banknote — from raw material to retirement.
          </p>
        </div>
        <div className="text-xs font-mono text-slate-500 border border-slate-200 px-3 py-2">
          Standard: ISO 14067 · Boundary: Cradle-to-Grave
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="kpi-total-emissions" icon={Activity} label="Total Emissions" value={`${data.total_emissions_tco2e.toLocaleString()} tCO₂e`} sub={`${data.total_batches} batches · ${data.total_notes_accepted.toLocaleString()} notes`} />
        <Stat testid="kpi-avg-pcf" icon={Banknote} label="Avg PCF / note" value={`${data.avg_pcf_per_note_kgco2e} kg`} sub="kgCO₂e per accepted note" />
        <Stat testid="kpi-polymer-savings" icon={TrendingDown} label="Polymer vs Paper" value={`${data.polymer_savings_pct}%`} sub={`Polymer ${data.polymer_avg_pcf} vs Paper ${data.paper_avg_pcf}`} accent="amber" />
        <Stat testid="kpi-supplier-conf" icon={Award} label="Supplier Confidence" value={`${data.supplier_avg_confidence}/100`} sub="Weighted across suppliers" />
        <Stat testid="kpi-anomalies" icon={AlertTriangle} label="Anomalies" value={data.anomalies_count} sub="Batches needing review" accent="amber" />
        <Stat testid="kpi-assurance" icon={ShieldCheck} label="ESG Assurance Readiness" value={`${data.assurance_readiness_pct}%`} sub="Verified evidence ratio" />
        <Stat testid="kpi-recirculation" icon={Recycle} label="Recirculation Avoided" value={`${(data.total_emissions_tco2e * 0.18).toFixed(2)} t`} sub="Modeled offset (note-life × fit-rate)" accent="amber" />
        <Stat testid="kpi-batches" icon={Layers} label="Batches Tracked" value={data.total_batches} sub="Active in current period" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-slate-200 p-6 bg-white" data-testid="chart-trend">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="overline text-slate-500">Emissions trend</div>
              <div className="font-heading text-xl font-bold text-slate-900">tCO₂e over time</div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={data.trend} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="tco2e" stroke="#166534" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-slate-200 p-6 bg-white" data-testid="chart-denom">
          <div className="overline text-slate-500">By Denomination</div>
          <div className="font-heading text-xl font-bold text-slate-900 mb-4">tCO₂e per denomination</div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.by_denomination} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                <XAxis dataKey="denomination" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="total_tco2e" radius={[2, 2, 0, 0]}>
                  {data.by_denomination.map((_, i) => (
                    <Cell key={i} fill={i % 2 === 0 ? "#166534" : "#B45309"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Methodology strip */}
      <div className="border border-slate-200 p-6 bg-slate-50">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="overline text-emerald-800">Methodology</div>
            <div className="font-semibold text-slate-900 mt-1">ISO 14067:2018</div>
            <div className="text-slate-600 text-xs mt-1">Cradle-to-grave product carbon footprint.</div>
          </div>
          <div>
            <div className="overline text-emerald-800">Functional Unit</div>
            <div className="font-semibold text-slate-900 mt-1">kgCO₂e / accepted note</div>
            <div className="text-slate-600 text-xs mt-1">Also tracked: per note-year of circulation.</div>
          </div>
          <div>
            <div className="overline text-emerald-800">Data Hierarchy</div>
            <div className="font-semibold text-slate-900 mt-1">Primary → 3rd-party → Secondary</div>
            <div className="text-slate-600 text-xs mt-1">Confidence-scored at batch and supplier level.</div>
          </div>
          <div>
            <div className="overline text-emerald-800">Assurance</div>
            <div className="font-semibold text-slate-900 mt-1">Internal · External</div>
            <div className="text-slate-600 text-xs mt-1">Audit-ready evidence pack per batch.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
